"use client";

import { ShowWithComedian } from "@punchup/data/legacy";
import { WAITLIST_QUERY_KEYS } from "@punchup/data/client";
import { SignInSignUpForm } from "../../components/signin";
import WaitlistModalBase from "./WaitlistModalBase";
import dayjs from "@punchup/utils/dayjs";
import { toast } from "../../shadcn/ui/use-toast";
import { usePathname } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";

const JoinIcon = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="18" cy="18" r="18" fill="#FE5E25" />
    <path
      d="M10.6675 26.9144C10.8075 26.9723 10.9537 27 11.1 27V26.9988C11.3775 26.9988 11.6513 26.8975 11.86 26.7047L14.7737 24.0179H25.12C26.7075 24.0179 28 22.7716 28 21.2408V20.3054H18.7939C18.5283 20.3054 18.313 20.0956 18.313 19.8368V19.7029C18.313 19.4441 18.5283 19.2343 18.7939 19.2343H28V17.5607H18.7939C18.5283 17.5607 18.313 17.3509 18.313 17.0921V16.9582C18.313 16.6993 18.5283 16.4895 18.7939 16.4895H28V14.8159H18.7939C18.5283 14.8159 18.313 14.6061 18.313 14.3473V14.2134C18.313 13.9546 18.5283 13.7448 18.7939 13.7448H27.9998C27.9817 12.2276 26.6963 11 25.12 11H12.88C11.2913 11 10 12.2451 10 13.7772V25.9393C10 26.3648 10.2613 26.7469 10.6675 26.9144Z"
      fill="white"
    />
  </svg>
);

export default function WaitlistLoginModal({
  isOpen,
  setIsOpen,
  show,
  ownerId,
  isVenue,
  onJoinSuccess,
  comedianSlug,
  venueSlug,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  show: ShowWithComedian;
  ownerId: string;
  isVenue?: boolean;
  onJoinSuccess?: () => void;
  comedianSlug?: string;
  venueSlug?: string;
}) {
  const queryClient = useQueryClient();
  const pathname = usePathname();

  const handleAuthComplete = async () => {
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      const datetime =
        typeof show.datetime === "string"
          ? dayjs(show.datetime)
          : show.datetime;

      const requestBody = {
        showName:
          show.title ||
          show.venue + (datetime ? datetime.format(" MMM D") : ""),
        receiveUpdates: true,
        showId: show.id,
        ownerId: ownerId,
        isVenue: isVenue || false,
      };

      const response = await fetch("/api/v2/user/waitlist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        if (response.status === 409) {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: [WAITLIST_QUERY_KEYS.perShow],
              refetchType: "active",
            }),
            queryClient.invalidateQueries({
              queryKey: [WAITLIST_QUERY_KEYS.batched],
              refetchType: "active",
            }),
          ]);

          toast({
            title: "You're already on the waitlist! 🎉",
            description:
              "We'll notify you when tickets become available for this show.",
            variant: "success",
          });

          onJoinSuccess?.();
          setIsOpen(false);
          return;
        }
        throw new Error("Failed to join waitlist");
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [WAITLIST_QUERY_KEYS.perShow],
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: [WAITLIST_QUERY_KEYS.batched],
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: [WAITLIST_QUERY_KEYS.dashboard],
        }),
      ]);

      toast({
        title: "You're on the waitlist! 🎉",
        description:
          "We'll notify you when tickets become available for this show.",
        variant: "success",
      });

      onJoinSuccess?.();
      setIsOpen(false);
    } catch (error) {
      toast({
        title: "Failed to join waitlist",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!isOpen) return null;

  const datetime =
    typeof show.datetime === "string" ? dayjs(show.datetime) : show.datetime;
  const showName =
    show.title || show.venue + (datetime ? datetime.format(" MMM D") : "");
  const waitlistParams = new URLSearchParams({
    joinWaitlist: "true",
    showId: show.id,
    showName: showName,
    ownerId: ownerId,
    isVenue: String(isVenue || false),
  });

  let redirectTo: string | null = null;

  if (pathname) {
    // If we're already on a page, redirect back to it with waitlist params
    redirectTo = `${pathname}?${waitlistParams.toString()}`;
  } else if (isVenue && venueSlug) {
    redirectTo = `/v/${venueSlug}?${waitlistParams.toString()}`;
  } else if (comedianSlug) {
    redirectTo = `/${comedianSlug}?${waitlistParams.toString()}`;
  }

  return (
    <WaitlistModalBase
      show={show}
      onClose={() => setIsOpen(false)}
      badgeIcon={<JoinIcon />}
    >
      <div className="text-center mb-6">
        <p className="text-xl font-semibold mb-2">Join the Waitlist</p>
        <p className="text-gray-500 text-sm">
          Join the waitlist to get notified about new dates and be the first to
          grab your tickets!
        </p>
      </div>

      <SignInSignUpForm
        buttonText="Sign in to Join Waitlist"
        policyFooterType="default"
        redirectTo={redirectTo}
        handleAuthComplete={handleAuthComplete}
      />
    </WaitlistModalBase>
  );
}
